"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { getPusherClient } from "@/lib/pusher-client";
import type { PresenceChannel, Members } from "pusher-js";

interface UserInfo {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
}

/**
 * Hook to track user online presence
 * @param enabled - Whether to enable presence tracking (default: true)
 * @returns Object with onlineUsers map and methods to check if users are online
 */
export function usePresence(enabled: boolean = true) {
  const { data: session, status } = useSession();
  const [onlineUsers, setOnlineUsers] = useState<Map<string, UserInfo>>(new Map());
  const [channel, setChannel] = useState<PresenceChannel | null>(null);

  useEffect(() => {
    // Only subscribe if enabled, authenticated, and session is ready
    if (!enabled || status !== "authenticated" || !session?.user?.id) {
      return;
    }

    const pusher = getPusherClient();

    // Subscribe to presence channel
    const presenceChannel = pusher.subscribe("presence-online") as PresenceChannel;

    // Initial members
    presenceChannel.bind("pusher:subscription_succeeded", (members: Members) => {
      const usersMap = new Map<string, UserInfo>();
      members.each((member: any) => {
        if (member.info) {
          usersMap.set(member.id, member.info);
        }
      });
      setOnlineUsers(usersMap);
    });

    // Member added
    presenceChannel.bind("pusher:member_added", (member: any) => {
      if (member.info) {
        setOnlineUsers((prev) => {
          const updated = new Map(prev);
          updated.set(member.id, member.info);
          return updated;
        });
      }
    });

    // Member removed
    presenceChannel.bind("pusher:member_removed", (member: any) => {
      setOnlineUsers((prev) => {
        const updated = new Map(prev);
        updated.delete(member.id);
        return updated;
      });
    });

    setChannel(presenceChannel);

    // Cleanup
    return () => {
      presenceChannel.unbind_all();
      presenceChannel.unsubscribe();
    };
  }, [enabled, session?.user?.id, status]);

  return {
    onlineUsers,
    isUserOnline: (userId: string) => onlineUsers.has(userId),
    getOnlineUserInfo: (userId: string) => onlineUsers.get(userId),
    channel,
  };
}
